import { Search as SearchIcon, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

export interface SearchProps {
  /** Current value of the search input. Controlled. */
  value: string;
  /** Callback fired with the new string when the input value changes. */
  onChange: (next: string) => void;
  /** Placeholder text. Default `"Search"`. */
  placeholder?: string;
  /**
   * Optional explicit clear handler. If omitted, clicking the trailing X
   * delegates to `onChange('')`.
   */
  onClear?: () => void;
  /** Optional className appended to the root wrapper. */
  className?: string;
  /** Optional aria-label for the underlying input. Defaults to `placeholder`. */
  'aria-label'?: string;
}

/**
 * Search atom — compact 26×26 search input per Figma `289:50552`.
 *
 * Borderless, fill-only background. Leading magnifier icon (14px) is always
 * visible; trailing X icon appears only when `value !== ''` and clears the
 * field via `onClear` (or `onChange('')` if `onClear` is not provided). Hover
 * state swaps the background to `bg-grey-100`. Designed for chrome/toolbar
 * placement; not a form input.
 *
 * Composed around the existing visual scale of the design system rather than
 * the 40-px `Input` atom, since the Figma scale (26 × 180) is intentionally
 * compact.
 */
export const Search = React.forwardRef<HTMLInputElement, SearchProps>(
  (
    { value, onChange, placeholder = 'Search', onClear, className, 'aria-label': ariaLabel },
    ref
  ) => {
    const handleClear = () => {
      if (onClear) {
        onClear();
      } else {
        onChange('');
      }
    };

    return (
      <div
        data-search-root
        className={cn(
          'inline-flex h-[26px] w-[180px] items-center gap-1 rounded bg-white px-1 py-2',
          'transition-colors duration-150',
          'hover:bg-grey-100 focus-within:bg-white',
          className
        )}
      >
        <SearchIcon
          data-search-magnifier
          aria-hidden="true"
          className={cn(
            'h-[14px] w-[14px] shrink-0',
            value === '' ? 'text-grey-400' : 'text-dawn-black'
          )}
        />
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          className={cn(
            'min-w-0 flex-1 bg-transparent font-outfit text-[13px] leading-[1.3]',
            'text-dawn-black placeholder:text-grey-400',
            'focus:outline-none'
          )}
        />
        {value !== '' && (
          <button
            data-search-clear
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="inline-flex h-3 w-3 shrink-0 items-center justify-center text-dawn-black hover:text-dawn-orange focus:outline-none"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
);
Search.displayName = 'Search';
